import { useEffect, useState } from "react";

type SetState<T> = React.Dispatch<React.SetStateAction<T>>;

function isIsoDateString(value: any): boolean {
  return (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)
  );
}

export function useSessionStorageState<T>(
  key: string,
  initialValue: T,
  parse?: (raw: unknown) => T | null,
): [T, SetState<T>] {
  const readValue = (): T => {
    try {
      const stored = globalThis.sessionStorage.getItem(key);
      if (stored === null) {
        return initialValue;
      }
      const parsed = JSON.parse(stored) as unknown;
      // If initialValue is a Date, parse ISO string to Date
      if (initialValue instanceof Date && isIsoDateString(parsed)) {
        return new Date(parsed as string) as T;
      }

      if (parse) {
        return parse(parsed) ?? initialValue;
      }

      return parsed as T;
    } catch {
      return initialValue;
    }
  };

  const [state, setState] = useState<T>(readValue);

  useEffect(() => {
    try {
      globalThis.sessionStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`Error writing sessionStorage key "${key}":`, error);
    }
  }, [key, state]);

  return [state, setState];
}
